import React from "react";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// Stack order bottom → top. Checked with the dataviz palette validator (light surface).
export const CHART_STATUSES = ["sent", "delivered", "read", "failed", "received"];
export const CHART_COLORS = {
  sent: "#4a7fe0",
  delivered: "#16a34a",
  read: "#8b5cf6",
  failed: "#e0413a",
  received: "#d98a0b",
};

const INK = "#4b5563";
const GRID = "rgba(0, 0, 0, 0.06)";
const label = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const pct = (part, whole) => (whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : "–");

// "2026-10-13" -> "13 Oct"
const shortDate = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y) return iso;
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

// Only statuses that actually occur, so an outbound report has no empty "Received" legend entry.
const activeStatuses = (rows) => CHART_STATUSES.filter((s) => rows.some((r) => Number(r[s]) > 0));

const stackedDatasets = (rows, statuses, horizontal = false) =>
  statuses.map((s) => ({
    label: label(s),
    data: rows.map((r) => Number(r[s]) || 0),
    backgroundColor: CHART_COLORS[s],
    borderColor: "#fff",
    // 2px surface gap between stacked segments
    borderWidth: horizontal ? { top: 0, right: 2, bottom: 0, left: 0 } : { top: 2, right: 0, bottom: 0, left: 0 },
    borderRadius: 4,
    borderSkipped: "start",
    maxBarThickness: 36,
  }));

// Tooltip footer uses the row's own total: a status outside the stacked keys (e.g. "pending")
// only counts there, so adding up the segments can come out lower.
const stackedOptions = (rows, horizontal = false, extraFooter = null) => ({
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  ...(horizontal ? { indexAxis: "y" } : {}),
  scales: stackedScales(horizontal),
  plugins: {
    legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 8, color: INK } },
    tooltip: {
      callbacks: {
        footer: (items) => {
          const row = rows[items[0]?.dataIndex];
          const total = Number(row?.total) || items.reduce((sum, i) => sum + (Number(i.raw) || 0), 0);
          return [`Total: ${total}`, ...(extraFooter && row ? [extraFooter(row)] : [])];
        },
      },
    },
  },
});

const templateName = (t) => t.template_name || "Unknown template";
const numberName = (n) => n.label || n.name || n.number_id || "Unknown number";

// "13 Oct – 19 Oct" from the dates the API actually used
const rangeLabel = (filters) =>
  filters?.from_date && filters?.to_date ? `${shortDate(filters.from_date)} – ${shortDate(filters.to_date)}` : "";

function stackedScales(horizontal = false) {
  const value = { stacked: true, beginAtZero: true, ticks: { precision: 0, color: INK }, grid: { color: GRID }, border: { display: false } };
  const category = { stacked: true, ticks: { color: INK, autoSkip: true, maxRotation: 0 }, grid: { display: false } };
  return horizontal ? { x: value, y: category } : { x: category, y: value };
}

const Panel = ({ title, sub, children, className = "col-lg-6" }) => (
  <div className={`${className} mb-4`}>
    <div className="panel panel-default with-rounded-corner with-shadow h-100">
      <div className="panel-heading">
        <h4 className="panel-title">
          {title}
          {sub && <small className="text-muted" style={{ marginLeft: 8, fontWeight: 400 }}>{sub}</small>}
        </h4>
      </div>
      <div className="panel-body p-3">{children}</div>
    </div>
  </div>
);

const Empty = ({ text }) => <p className="text-muted text-center py-5 mb-0">{text}</p>;

const ChartSkeleton = ({ height }) => <div className="skeleton-text" style={{ height, width: "100%", borderRadius: 8 }} />;

const num = (v) => Number(v) || 0;
const fmt = (v) => num(v).toLocaleString("en-IN");

/**
 * Four equal summary cards. Each status card carries its own rate:
 *   Delivered = delivered + read, rate out of all sent
 *   Read      = read, rate out of delivered
 *   Failed    = failed, rate out of all sent
 * Counts are for the whole filter (summary.by_status), not the current page.
 */
export function SummaryCards({ summary, loading, activeStatus, onPick }) {
  const s = summary?.by_status || {};
  const sentTotal = num(s.sent) + num(s.delivered) + num(s.read) + num(s.failed);
  const delivered = num(s.delivered) + num(s.read);

  const cards = [
    {
      key: "",
      title: "Total messages",
      value: summary?.total,
      color: "#334155",
      lines: [
        num(s.sent) ? `${fmt(s.sent)} awaiting delivery` : "All sent messages have a final status",
        num(s.received) ? `${fmt(s.received)} received from customers` : null,
      ],
      hint: "Show every status",
    },
    {
      key: "delivered,read",
      title: "Delivered",
      value: delivered,
      color: CHART_COLORS.delivered,
      rate: pct(delivered, sentTotal),
      ratio: sentTotal ? delivered / sentTotal : 0,
      rateLabel: "delivery rate",
      lines: [`${fmt(s.delivered)} not read yet`],
      hint: "Delivered or read, out of all sent",
    },
    {
      key: "read",
      title: "Read",
      value: s.read,
      color: CHART_COLORS.read,
      rate: pct(num(s.read), delivered),
      ratio: delivered ? num(s.read) / delivered : 0,
      rateLabel: "read rate",
      lines: [`of ${fmt(delivered)} delivered`],
      hint: "Read, out of delivered",
    },
    {
      key: "failed",
      title: "Failed",
      value: s.failed,
      color: CHART_COLORS.failed,
      rate: pct(num(s.failed), sentTotal),
      ratio: sentTotal ? num(s.failed) / sentTotal : 0,
      rateLabel: "failure rate",
      lines: [`of ${fmt(sentTotal)} sent`],
      hint: "Failed, out of all sent",
    },
  ];

  return (
    <div className="row mb-4">
      {cards.map((c) => {
        const active = c.key ? activeStatus === c.key : !activeStatus;
        return (
          <div key={c.title} className="col-lg-3 col-md-6 mb-3 mb-lg-0">
            <div
              role="button"
              tabIndex={0}
              onClick={() => onPick(c.key)}
              onKeyDown={(e) => e.key === "Enter" && onPick(c.key)}
              title={c.key && active ? "Clear status filter" : c.hint}
              style={{
                height: "100%",
                padding: "14px 16px",
                background: "#fff",
                borderRadius: 8,
                border: `1px solid ${active && c.key ? c.color : "#e5e7eb"}`,
                borderLeft: `4px solid ${c.color}`,
                boxShadow: active && c.key ? `0 0 0 2px ${c.color}33` : "0 1px 2px rgba(0,0,0,0.04)",
                cursor: "pointer",
              }}
            >
              <div className="d-flex justify-content-between align-items-start">
                <div style={{ fontSize: 12, fontWeight: 600, color: INK, textTransform: "uppercase", letterSpacing: 0.4 }}>{c.title}</div>
                {c.rate && (
                  <div style={{ textAlign: "right", lineHeight: 1.1 }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>{loading ? "…" : c.rate}</div>
                    <div style={{ fontSize: 11, color: INK }}>{c.rateLabel}</div>
                  </div>
                )}
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: "#111827", lineHeight: 1.2, marginTop: c.rate ? -6 : 4 }}>
                {loading ? "…" : fmt(c.value)}
              </div>
              {c.rate && (
                <div style={{ height: 6, background: "#f1f5f9", borderRadius: 3, margin: "8px 0 6px", overflow: "hidden" }}>
                  <div style={{ width: `${loading ? 0 : Math.min(100, c.ratio * 100)}%`, height: "100%", background: c.color, borderRadius: 3, transition: "width .3s" }} />
                </div>
              )}
              {!loading &&
                c.lines.filter(Boolean).map((line) => (
                  <div key={line} style={{ fontSize: 12, color: INK, marginTop: c.rate ? 0 : 4 }}>{line}</div>
                ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Charts above the report table.
 * `stats` comes from GET report/messages/stats; when that call fails (e.g. not deployed yet)
 * the per-day/template/reason charts show a note and the status split still works from `summary`.
 */
export function ReportCharts({ stats, statsError, statsLoading, summary, loading }) {
  const byDay = stats?.by_day || [];
  const byNumber = stats?.by_number || [];
  const byTemplate = stats?.by_template || [];
  const reasons = stats?.failure_reasons || [];

  const byStatus = summary?.by_status || {};
  const statusKeys = CHART_STATUSES.filter((s) => Number(byStatus[s]) > 0);

  const unavailable = statsError ? <Empty text={`Chart data could not be loaded: ${statsError}`} /> : null;

  const dayStatuses = activeStatuses(byDay);
  const templateStatuses = activeStatuses(byTemplate);
  const numberStatuses = activeStatuses(byNumber);

  return (
    <>
      <div className="row">
        <Panel title="Messages per day" sub={["by status", rangeLabel(stats?.filters)].filter(Boolean).join(" · ")} className="col-lg-8">
          {statsLoading ? (
            <ChartSkeleton height={280} />
          ) : unavailable || (byDay.length === 0 || dayStatuses.length === 0 ? (
            <Empty text="No messages in this date range" />
          ) : (
            <div style={{ height: 280, position: "relative" }}>
              <Bar
                data={{ labels: byDay.map((d) => shortDate(d.date)), datasets: stackedDatasets(byDay, dayStatuses) }}
                options={stackedOptions(byDay)}
              />
            </div>
          ))}
        </Panel>

        <Panel title="Status split" sub="whole filter" className="col-lg-4">
          {loading ? (
            <ChartSkeleton height={280} />
          ) : statusKeys.length === 0 ? (
            <Empty text="No messages" />
          ) : (
            <div style={{ height: 280, position: "relative" }}>
              <Doughnut
                data={{
                  labels: statusKeys.map(label),
                  datasets: [{
                    data: statusKeys.map((s) => Number(byStatus[s]) || 0),
                    backgroundColor: statusKeys.map((s) => CHART_COLORS[s]),
                    borderColor: "#fff",
                    borderWidth: 2,
                    hoverOffset: 4,
                  }],
                }}
                options={{
                  maintainAspectRatio: false,
                  cutout: "68%",
                  plugins: {
                    legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 8, color: INK } },
                    tooltip: {
                      callbacks: {
                        label: (ctx) => `${ctx.label}: ${ctx.raw} (${pct(ctx.raw, summary?.total || 0)})`,
                      },
                    },
                  },
                }}
              />
            </div>
          )}
        </Panel>
      </div>

      <div className="row">
        <Panel title="Top templates" sub="top 10 by messages · hover for read rate">
          {statsLoading ? (
            <ChartSkeleton height={300} />
          ) : unavailable || (byTemplate.length === 0 ? (
            <Empty text="No template messages" />
          ) : (
            <div style={{ height: Math.max(180, byTemplate.length * 30 + 70), position: "relative" }}>
              <Bar
                data={{ labels: byTemplate.map(templateName), datasets: stackedDatasets(byTemplate, templateStatuses, true) }}
                options={stackedOptions(byTemplate, true, (t) => `Read rate: ${pct(Number(t.read) || 0, Number(t.total) || 0)}`)}
              />
            </div>
          ))}
        </Panel>

        <Panel title="Why messages failed" sub="top 10 reasons">
          {statsLoading ? (
            <ChartSkeleton height={300} />
          ) : unavailable || (reasons.length === 0 ? (
            <Empty text="No failed messages" />
          ) : (
            <div style={{ height: Math.max(180, reasons.length * 30 + 40), position: "relative" }}>
              <Bar
                data={{
                  labels: reasons.map((r) => (r.code ? `${r.code} · ` : "") + (r.reason || "Unknown")),
                  datasets: [{
                    label: "Failed messages",
                    data: reasons.map((r) => Number(r.count) || 0),
                    backgroundColor: CHART_COLORS.failed,
                    borderRadius: 4,
                    maxBarThickness: 22,
                  }],
                }}
                options={{
                  maintainAspectRatio: false,
                  indexAxis: "y",
                  plugins: {
                    legend: { display: false },
                    tooltip: { callbacks: { title: (items) => reasons[items[0].dataIndex]?.reason || "" } },
                  },
                  scales: {
                    x: { beginAtZero: true, ticks: { precision: 0, color: INK }, grid: { color: GRID }, border: { display: false } },
                    y: {
                      ticks: {
                        color: INK,
                        // Long reasons are cut on the axis; the tooltip shows the full text.
                        callback: function (value) {
                          const text = this.getLabelForValue(value);
                          return text.length > 42 ? `${text.slice(0, 40)}…` : text;
                        },
                      },
                      grid: { display: false },
                    },
                  },
                }}
              />
            </div>
          ))}
        </Panel>
      </div>

      {byNumber.length > 1 && (
        <div className="row">
          <Panel title="By business number" className="col-lg-12">
            <div style={{ height: Math.max(160, byNumber.length * 34 + 70), position: "relative" }}>
              <Bar
                data={{ labels: byNumber.map(numberName), datasets: stackedDatasets(byNumber, numberStatuses, true) }}
                options={stackedOptions(byNumber, true)}
              />
            </div>
          </Panel>
        </div>
      )}
    </>
  );
}
